import React, { useEffect, useState, useContext } from 'react';
import axios from 'axios';
import { AuthContext } from '../App';
import Navbar from '../components/Navbar';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';

export default function PaymentsPage() {
  const { user } = useContext(AuthContext);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    fetchPaymentStatus();
  }, []);

  const fetchPaymentStatus = async () => {
    try {
      const response = await axios.get('/payments/status');
      setPaymentStatus(response.data);
    } catch (error) {
      console.error('Failed to fetch payment status', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePayment = async (method) => {
    setProcessing(true);

    try {
      await axios.post('/payments/create', {
        payment_method: method,
        amount: 100.00  // Quota annuale esempio
      });

      if (method === 'stripe') {
        toast.success('Integrazione Stripe in fase di sviluppo. Pagamento registrato.');
      } else if (method === 'paypal') {
        toast.success('Integrazione PayPal in fase di sviluppo. Pagamento registrato.');
      }

      // Refresh status
      setTimeout(() => {
        fetchPaymentStatus();
      }, 1000);
    } catch (error) {
      toast.error('Errore nella creazione del pagamento');
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div className="min-h-screen bg-white" data-testid="payments-page">
      <Navbar />

      <div className="container mx-auto px-6 pt-24 pb-12 max-w-4xl">
        <h1 className="text-4xl font-bold mb-12" data-testid="page-title">Pagamento Quota Associativa</h1>

        {loading ? (
          <div className="text-center py-12">Caricamento...</div>
        ) : (
          <div className="space-y-8">
            {/* Payment Status */}
            <Card data-testid="payment-status-card">
              <CardHeader>
                <CardTitle>Stato Pagamento</CardTitle>
              </CardHeader>
              <CardContent>
                {paymentStatus?.has_paid ? (
                  <div className="flex items-center gap-3">
                    <div className="w-3 h-3 bg-green-600 rounded-full"></div>
                    <span className="font-semibold">Pagamento effettuato</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-3">
                    <div className="w-3 h-3 bg-red-600 rounded-full"></div>
                    <span className="font-semibold">Pagamento in attesa</span>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Payment Methods */}
            {!paymentStatus?.has_paid && (
              <Card data-testid="payment-methods-card">
                <CardHeader>
                  <CardTitle>Scegli Metodo di Pagamento</CardTitle>
                  <CardDescription>Quota annuale: €100,00</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Button
                      onClick={() => handlePayment('stripe')}
                      disabled={processing}
                      className="h-24 bg-black text-white hover:bg-gray-800 text-lg"
                      data-testid="stripe-payment-button"
                    >
                      <div className="flex flex-col items-center">
                        <svg className="w-16 h-8 mb-2" viewBox="0 0 60 25" xmlns="http://www.w3.org/2000/svg">
                          <path fill="#fff" d="M59.64 14.28h-8.06c.19 1.93 1.6 2.55 3.2 2.55 1.64 0 2.96-.37 4.05-.95v3.32a8.33 8.33 0 01-4.56 1.1c-4.01 0-6.83-2.5-6.83-7.48 0-4.19 2.39-7.52 6.3-7.52 3.92 0 5.96 3.28 5.96 7.5 0 .4-.04 1.26-.06 1.48zm-5.92-5.62c-1.03 0-2.17.73-2.17 2.58h4.25c0-1.85-1.07-2.58-2.08-2.58zM40.95 20.3c-1.44 0-2.32-.6-2.9-1.04l-.02 4.63-4.12.87V5.57h3.76l.08 1.02a4.7 4.7 0 013.23-1.29c2.9 0 5.62 2.6 5.62 7.4 0 5.23-2.7 7.6-5.65 7.6zM40 8.95c-.95 0-1.54.34-1.97.81l.02 6.12c.4.44.98.78 1.95.78 1.52 0 2.54-1.65 2.54-3.87 0-2.15-1.04-3.84-2.54-3.84zM28.24 5.57h4.13v14.44h-4.13V5.57zm0-4.7L32.37 0v3.36l-4.13.88V.88zm-4.32 9.35v9.79H19.8V5.57h3.7l.12 1.22c1-1.77 3.07-1.41 3.62-1.22v3.79c-.52-.17-2.29-.43-3.32.86zm-8.55 4.72c0 2.43 2.6 1.68 3.12 1.46v3.36c-.55.3-1.54.54-2.89.54a4.15 4.15 0 01-4.27-4.24l.01-13.17 4.02-.86v3.54h3.14V9.1h-3.13v5.85zm-4.91.7c0 2.97-2.31 4.66-5.73 4.66a11.2 11.2 0 01-4.46-.93v-3.93c1.38.75 3.1 1.31 4.46 1.31.92 0 1.53-.24 1.53-1C6.26 13.77 0 14.51 0 9.95 0 7.04 2.28 5.3 5.62 5.3c1.36 0 2.72.2 4.09.75v3.88a9.23 9.23 0 00-4.1-1.06c-.86 0-1.44.25-1.44.9 0 1.85 6.29.97 6.29 5.88z"/>
                        </svg>
                        <span>Paga con Stripe</span>
                      </div>
                    </Button>

                    <Button
                      onClick={() => handlePayment('paypal')}
                      disabled={processing}
                      className="h-24 bg-blue-600 text-white hover:bg-blue-700 text-lg"
                      data-testid="paypal-payment-button"
                    >
                      <div className="flex flex-col items-center">
                        <svg className="w-16 h-8 mb-2" viewBox="0 0 100 32" xmlns="http://www.w3.org/2000/svg">
                          <path fill="#fff" d="M12 4.917L4.2 26.333h4.8L11.6 21.5h4.8l.64-2.6h-4.8l3.2-11.1h6.4c1.6 0 2.4.8 2.4 2.4 0 .4 0 .8-.16 1.2l-2.4 9.6c-.16.8-.16 1.6 0 2.4.4.8 1.2 1.2 2.4 1.2h.8l.64-2.6h-.8c-.4 0-.64-.16-.64-.64 0-.16 0-.4.16-.64l2.4-9.6c.16-.8.24-1.6.24-2.4 0-2.8-1.6-4.8-5.6-4.8H12zm32 0L36.2 26.333h4.8L43.6 21.5h4.8l.64-2.6h-4.8l3.2-11.1h6.4c1.6 0 2.4.8 2.4 2.4 0 .4 0 .8-.16 1.2l-2.4 9.6c-.16.8-.16 1.6 0 2.4.4.8 1.2 1.2 2.4 1.2h.8l.64-2.6h-.8c-.4 0-.64-.16-.64-.64 0-.16 0-.4.16-.64l2.4-9.6c.16-.8.24-1.6.24-2.4 0-2.8-1.6-4.8-5.6-4.8H44z"/>
                        </svg>
                        <span>Paga con PayPal</span>
                      </div>
                    </Button>
                  </div>
                  <p className="text-sm text-gray-600 mt-4 text-center">
                    Le integrazioni di pagamento sono in fase di sviluppo.
                  </p>
                </CardContent>
              </Card>
            )}

            {/* Payment Info */}
            <Card data-testid="payment-info-card">
              <CardHeader>
                <CardTitle>Informazioni sul Pagamento</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3 text-gray-700">
                  <p><span className="font-semibold">Quota annuale:</span> €100,00</p>
                  <p><span className="font-semibold">Benefici:</span> Accesso completo a tutte le attività, eventi e network dell'associazione</p>
                  <p><span className="font-semibold">Rinnovo:</span> Annuale</p>
                  <p className="text-sm text-gray-600 pt-4">
                    Per domande o supporto sui pagamenti, contatta l'amministrazione dell'associazione.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
}
